import { canCreateCompany, hasActiveCompany, permission, trialExpired, emailNotConfirmed, mustConfirmEmail, mustCompleteOnboarding, onboardingCompleted, } from '@/middlewares.ts';
import Dashboard from '@/layout/dashboard/wrapper.vue';
import { landingRoute } from '@/landing';
export const globalRoutes = () => [
    {
        path: '/auth',
        component: () => import('@/layout/auth/wrapper.vue'),
        meta: {
            auth: {
                redirectGuest: { name: 'home' },
            },
        },
        children: [
            {
                name: 'login',
                path: 'login',
                component: () => import('@/pages/auth/login.vue'),
            },
            {
                name: 'register',
                path: 'register',
                component: () => import('@/pages/auth/register.vue'),
            },
            {
                name: 'remind',
                path: 'remind',
                component: () => import('@/pages/auth/remind.vue'),
            },
            {
                name: 'reset',
                path: 'reset',
                props: true,
                component: () => import('@/pages/auth/reset.vue'),
                beforeEnter(to) {
                    if (!to.query.payload) {
                        return { name: 'login' };
                    }
                    try {
                        const json = JSON.parse(atob(to.query.payload));
                        if (!json.email || !json.token) {
                            return { name: 'login' };
                        }
                        to.params.token = json.token;
                        to.params.email = json.email;
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    }
                    catch (ex) {
                        return { name: 'login' };
                    }
                    return true;
                },
            },
            {
                name: 'deleted-profile',
                path: 'deleted-profile',
                component: () => import('@/pages/profile/deleted-profile.vue'),
            },
        ],
    },
    {
        path: '/direct-payment-status',
        component: () => import('@/pages/direct-payment/redirect.vue'),
    },
];
export const emailConfirmRoutes = () => [
    {
        path: '/register-confirmation',
        meta: {
            auth: {
                required: true,
                redirectGuest: { name: 'login' },
            },
        },
        children: [
            {
                name: 'register-confirmation',
                path: '/register-confirmation',
                component: () => import('@/pages/auth/email-confirm.vue'),
                meta: {
                    middleware: [emailNotConfirmed],
                },
                props: true,
                beforeEnter(to) {
                    if (to.query.redirect) {
                        to.params.redirect = to.query.redirect;
                    }
                    return true;
                },
            },
            {
                name: 'register-confirmation.email-edit',
                path: '/register-confirmation.email-edit',
                component: () => import('@/pages/auth/email-change.vue'),
                meta: {
                    middleware: [emailNotConfirmed],
                },
            },
        ],
    },
];
export const protectedRoutes = () => [
    {
        path: '/dashboard',
        component: Dashboard,
        meta: {
            auth: {
                required: true,
                redirectGuest: { name: 'login' },
            },
            scroll: {
                ignoreQuery: ['flt_compare_value', 'flt_interval_type', 'flt_interval', 'flt_compare'],
            },
            middleware: [
                mustConfirmEmail,
                mustCompleteOnboarding,
            ],
        },
        children: [
            // main page, resolved from the user's permissions
            {
                name: 'home',
                path: '/',
                redirect: () => landingRoute(),
            },
            // statistics
            {
                name: 'dashboard',
                path: '/statistics',
                component: () => import('@/pages/dashboard/dashboard.vue'),
                meta: {
                    scroll: {
                        ignoreScrollToTop: true,
                    },
                    middleware: [hasActiveCompany, permission('dashboard'), trialExpired],
                },
            },
            // invoices
            {
                path: '/invoices',
                meta: {
                    scroll: {
                        ignoreQuery: ['expanded'],
                    },
                    middleware: [hasActiveCompany, trialExpired, permission('invoice.index')],
                },
                children: [
                    {
                        name: 'invoice.index',
                        path: ':modal?',
                        component: () => import('@/pages/invoices/index.vue'),
                    },
                    {
                        name: 'invoice.create',
                        path: 'create',
                        component: () => import('@/pages/invoices/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'invoice.index.title', path: '/invoices' },
                            ],
                        },
                    },
                    {
                        name: 'invoice.edit',
                        path: ':id/:tab?/edit',
                        props: true,
                        component: () => import('@/pages/invoices/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'invoice.index.title', path: '/invoices' },
                            ],
                        },
                    },
                    {
                        name: 'import.invoices.create',
                        path: 'import/invoices',
                        component: () => import('@/pages/import/invoices/import-invoices-page.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'invoice.index.title', path: '/invoices' },
                            ],
                        },
                    },
                ],
            },
            // products
            {
                path: '/products',
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                },
                children: [
                    {
                        name: 'product.index',
                        path: '',
                        component: () => import('@/pages/products/index.vue'),
                    },
                    {
                        name: 'product.create',
                        path: 'create',
                        component: () => import('@/pages/products/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'product.index.title', path: '/products' },
                            ],
                        },
                    },
                    {
                        name: 'product.edit',
                        path: ':id/edit',
                        props: true,
                        component: () => import('@/pages/products/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'product.index.title', path: '/products' },
                            ],
                        },
                    },
                    {
                        name: 'product.warehouse-history',
                        path: ':id/warehouse-history',
                        props: true,
                        component: () => import('@/pages/products/warehouse-history-page.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'product.index.title', path: '/products' },
                            ],
                        },
                    },
                    {
                        name: 'import.products.create',
                        path: 'import',
                        component: () => import('@/pages/import/products/import-products-page.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'product.index.title', path: '/products' },
                            ],
                        },
                    },
                ],
            },
            // projects
            {
                path: '/projects',
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                },
                children: [
                    {
                        name: 'project.index',
                        path: '',
                        component: () => import('@/pages/projects/index.vue'),
                    },
                    {
                        name: 'project.create',
                        path: 'create',
                        component: () => import('@/pages/projects/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'project.index.title', path: '/projects' },
                            ],
                        },
                    },
                    {
                        name: 'project.edit',
                        path: ':id/edit',
                        props: true,
                        component: () => import('@/pages/projects/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'project.index.title', path: '/projects' },
                            ],
                        },
                    },
                ],
            },
            // modules
            {
                path: '/modules',
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                },
                children: [
                    {
                        name: 'module.index',
                        path: '',
                        component: () => import('@/pages/modules/index.vue'),
                    },
                    {
                        name: 'module.edit',
                        path: ':id/edit',
                        props: true,
                        component: () => import('@/pages/modules/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'module.title', path: '/modules' },
                            ],
                        },
                    },
                ],
            },
            // clients
            {
                path: '/clients',
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                },
                children: [
                    {
                        name: 'client.index',
                        path: '',
                        component: () => import('@/pages/clients/index.vue'),
                    },
                    {
                        name: 'client.create',
                        path: 'create',
                        component: () => import('@/pages/clients/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'client.index.title', path: '/clients' },
                            ],
                        },
                    },
                    {
                        name: 'client.edit',
                        path: ':id/edit',
                        props: true,
                        component: () => import('@/pages/clients/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'client.index.title', path: '/clients' },
                            ],
                        },
                    },
                    {
                        name: 'import.clients.create',
                        path: 'import',
                        component: () => import('@/pages/import/clients/import-clients-page.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'client.index.title', path: '/clients' },
                            ],
                        },
                    },
                ],
            },
            // commercial-suggestion
            {
                path: '/commercial-suggestions',
                meta: {
                    scroll: {
                        ignoreQuery: ['expanded'],
                    },
                    middleware: [hasActiveCompany],
                },
                children: [
                    {
                        name: 'commercial-suggestion.index',
                        path: '',
                        component: () => import('@/pages/commercial-suggestion/index.vue'),
                    },
                    {
                        name: 'commercial-suggestion.create',
                        path: 'create',
                        component: () => import('@/pages/commercial-suggestion/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'commercial-suggestion.index.title', path: '/commercial-suggestions' },
                            ],
                        },
                    },
                    {
                        name: 'commercial-suggestion.edit',
                        path: ':id/edit',
                        props: true,
                        component: () => import('@/pages/commercial-suggestion/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'commercial-suggestion.index.title', path: '/commercial-suggestions' },
                            ],
                        },
                    },
                ],
            },
            // company-contracts
            {
                path: '/company-contracts',
                meta: {
                    scroll: {
                        ignoreQuery: ['expanded'],
                    },
                    middleware: [hasActiveCompany],
                },
                children: [
                    {
                        name: 'company-contracts.index',
                        path: '',
                        component: () => import('@/pages/company-contracts/index.vue'),
                    },
                    {
                        name: 'company-contracts.create',
                        path: 'create',
                        component: () => import('@/pages/company-contracts/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'company-contract.index.title', path: '/company-contracts' },
                            ],
                        },
                    },
                    {
                        name: 'company-contracts.edit',
                        path: ':id/:tab?/edit',
                        props: true,
                        component: () => import('@/pages/company-contracts/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'company-contract.index.title', path: '/company-contracts' },
                            ],
                        },
                    },
                ],
            },
            // company-contracts-payments
            {
                path: '/company-contract-payments',
                meta: {
                    scroll: {
                        ignoreQuery: ['expanded'],
                    },
                    middleware: [hasActiveCompany],
                },
                children: [
                    {
                        name: 'company-contract-payments.index',
                        path: '',
                        component: () => import('@/pages/company-contract-payments/index.vue'),
                    },
                ],
            },
            // events
            {
                path: '/events',
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                },
                children: [
                    {
                        name: 'events.edit',
                        path: ':tab?',
                        props: true,
                        component: () => import('@/pages/events/manage.vue'),
                    },
                ],
            },
            // templates
            {
                path: '/templates',
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                },
                children: [
                    {
                        name: 'template.index',
                        path: '',
                        component: () => import('@/pages/templates/index.vue'),
                    },
                    {
                        name: 'template.create',
                        path: 'create',
                        component: () => import('@/pages/templates/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'template.index.title', path: '/templates' },
                            ],
                        },
                    },
                    {
                        name: 'template.edit',
                        path: ':id/edit',
                        props: true,
                        component: () => import('@/pages/templates/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'template.index.title', path: '/templates' },
                            ],
                        },
                    },
                ],
            },
            // notifications
            {
                name: 'notification.index',
                path: '/notifications',
                component: () => import('@/pages/notifications/index.vue'),
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                },
            },
            // roles
            {
                path: '/roles',
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                },
                children: [
                    {
                        name: 'role.index',
                        path: '',
                        component: () => import('@/pages/roles/index.vue'),
                    },
                    {
                        name: 'role.create',
                        path: 'create',
                        component: () => import('@/pages/roles/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'role.index.title', path: '/roles' },
                            ],
                        },
                    },
                    {
                        name: 'role.edit',
                        path: ':id/edit',
                        props: true,
                        component: () => import('@/pages/roles/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'role.index.title', path: '/roles' },
                            ],
                        },
                    },
                ],
            },
            // api
            {
                path: '/api',
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                    breadcrumb: [
                        { name: 'module.titles.api', path: '/modules/api/edit' },
                    ],
                },
                children: [
                    {
                        name: 'api.create',
                        path: 'create',
                        component: () => import('@/pages/api/manage.vue'),
                    },
                    {
                        name: 'api.edit',
                        path: ':id/edit',
                        props: true,
                        component: () => import('@/pages/api/manage.vue'),
                    },
                ],
            },
            // employees
            {
                name: 'employee.index',
                path: '/employees',
                component: () => import('@/pages/employees/index.vue'),
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                },
            },
            // receipts
            {
                name: 'receipt.index',
                path: '/receipts',
                component: () => import('@/pages/receipts/index.vue'),
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                },
            },
            // company create
            {
                name: 'company.create',
                path: '/company/create',
                component: () => import('@/pages/company/create.vue'),
                meta: {
                    middleware: [canCreateCompany],
                },
            },
            // company settings
            {
                path: '/company',
                meta: {
                    middleware: [hasActiveCompany],
                },
                children: [
                    {
                        name: 'company.edit',
                        path: ':tab?',
                        props: true,
                        component: () => import('@/pages/company/manage.vue'),
                    },
                ],
            },
            // expenses
            {
                path: '/expenses',
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                },
                children: [
                    {
                        name: 'expense.index',
                        path: '',
                        component: () => import('@/pages/expenses/index.vue'),
                    },
                    {
                        name: 'expense.create',
                        path: 'create',
                        component: () => import('@/pages/expenses/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'expense.index.title', path: '/expenses' },
                            ],
                        },
                    },
                    {
                        name: 'expense.edit',
                        path: ':id/:tab?/edit',
                        props: true,
                        component: () => import('@/pages/expenses/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'expense.index.title', path: '/expenses' },
                            ],
                        },
                    },
                ],
            },
            {
                path: '/expenses-new',
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                },
                children: [
                    {
                        name: 'expense-new.index',
                        path: '',
                        component: () => import('@/pages/expenses-new/index/index.vue'),
                    },
                    {
                        name: 'expense-new.create',
                        path: 'create',
                        component: () => import('@/pages/expenses-new/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'expense-new.index.title', path: '/expenses-new' },
                            ],
                        },
                    },
                    {
                        name: 'expense-new.import',
                        path: 'import',
                        component: () => import('@/pages/ocr/expense-new-ocr/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'expense-new.index.title', path: '/expenses-new' },
                            ],
                        },
                    },
                    {
                        name: 'expense-new.edit',
                        path: ':expenseId/:tab?/edit',
                        props: true,
                        component: () => import('@/pages/expenses-new/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'expense-new.index.title', path: '/expenses-new' },
                            ],
                        },
                    },
                ],
            },
            {
                path: '/expense-documents',
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                },
                children: [
                    {
                        name: 'expense-documents.index',
                        path: '',
                        component: () => import('@/pages/expense-documents/index.vue'),
                    },
                ],
            },
            {
                path: '/expense-document-import',
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                },
                children: [
                    {
                        name: 'expense-document-import.create',
                        path: '',
                        component: () => import('@/pages/ocr/expense-ocr/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'expense-document.index.title', path: '/expense-documents' },
                            ],
                        },
                    },
                ],
            },
            // periodic invoices
            {
                path: '/periodic-expenses',
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                    scroll: {
                        ignoreQuery: ['expanded'],
                    },
                },
                children: [
                    {
                        name: 'periodic-expense.index',
                        path: '',
                        component: () => import('@/pages/periodic-expenses/index.vue'),
                    },
                    {
                        name: 'periodic-expense.create',
                        path: 'create',
                        component: () => import('@/pages/periodic-expenses/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'periodic-expenses.index.title', path: '/periodic-expenses' },
                            ],
                        },
                    },
                    {
                        name: 'periodic-expense.edit',
                        path: ':id/edit',
                        props: true,
                        component: () => import('@/pages/periodic-expenses/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'periodic-expenses.index.title', path: '/periodic-expenses' },
                            ],
                        },
                    },
                ],
            },
            // fixed assets
            {
                name: 'fixed-asset.index',
                path: '/fixed-assets',
                component: () => import('@/pages/fixed-assets/index.vue'),
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                },
            },
            {
                name: 'fixed-asset.wears',
                path: '/fixed-assets/:id/wears',
                props: true,
                component: () => import('@/pages/fixed-assets/wears-page.vue'),
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                    breadcrumb: [
                        { name: 'fixed_asset.index.title', path: '/fixed-assets' },
                    ],
                },
            },
            // journal
            {
                name: 'journal.index',
                path: '/journal',
                component: () => import('@/pages/journal/index.vue'),
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                },
            },
            // periodic invoices
            {
                path: '/periodic-invoices',
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                    scroll: {
                        ignoreQuery: ['expanded'],
                    },
                },
                children: [
                    {
                        name: 'periodic-invoice.index',
                        path: '',
                        component: () => import('@/pages/periodic-invoices/index.vue'),
                    },
                    {
                        name: 'periodic-invoice.create',
                        path: 'create',
                        component: () => import('@/pages/periodic-invoices/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'periodic_invoice.index.title', path: '/periodic-invoices' },
                            ],
                        },
                    },
                    {
                        name: 'periodic-invoice.edit',
                        path: ':id/edit',
                        props: true,
                        component: () => import('@/pages/periodic-invoices/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'periodic_invoice.index.title', path: '/periodic-invoices' },
                            ],
                        },
                    },
                    {
                        name: 'import.periodic-invoices.create',
                        path: 'import/periodic-invoices',
                        component: () => import('@/pages/import/periodic-invoices/import-invoices-page.vue'),
                    },
                ],
            },
            // subscriptions
            {
                path: '/subscription',
                children: [
                    {
                        name: 'subscription.payment',
                        path: 'payment/:payment?',
                        props: true,
                        component: () => import('@/pages/subscription/redirect/redirect.vue'),
                    },
                    // for Stripe UI redirect to app when you cancel the payment
                    {
                        name: 'stripe.cancel',
                        path: 'stripe/cancel',
                        props: true,
                        component: () => import('@/pages/subscription/redirect/stripe-cancel.vue'),
                    },
                    // for Stripe UI redirect to app when you add a card
                    {
                        name: 'stripe.card.added',
                        path: 'stripe/card-added',
                        props: true,
                        component: () => import('@/pages/subscription/redirect/stripe-card-added.vue'),
                    },
                    {
                        name: 'subscription.checkout',
                        path: 'checkout',
                        props: true,
                        component: () => import('@/pages/subscription/checkout-v2/checkout-form-v2.vue'),
                    },
                ],
            },
            {
                name: 'empty',
                path: '/empty',
                component: () => import('@/pages/empty.vue'),
            },
            {
                name: 'no-active-company',
                path: '/no-active-company',
                component: () => import('@/pages/no-active-company.vue'),
            },
            // carriers
            {
                path: '/carriers',
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                },
                children: [
                    {
                        name: 'carrier.index',
                        path: '',
                        component: () => import('@/pages/carriers/index.vue'),
                    },
                    {
                        name: 'carrier.create',
                        path: 'create',
                        component: () => import('@/pages/carriers/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'carrier.index.title', path: '/carriers' },
                            ],
                        },
                    },
                    {
                        name: 'carrier.edit',
                        path: ':id/edit',
                        props: true,
                        component: () => import('@/pages/carriers/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'carrier.index.title', path: '/carriers' },
                            ],
                        },
                    },
                ],
            },
            // waybills
            {
                path: '/waybills',
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                    scroll: {
                        ignoreQuery: ['expanded'],
                    },
                },
                children: [
                    {
                        name: 'waybill.index',
                        path: '',
                        component: () => import('@/pages/waybills/index.vue'),
                    },
                    {
                        name: 'waybill.create',
                        path: 'create',
                        component: () => import('@/pages/waybills/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'waybill.index.title', path: '/waybills' },
                            ],
                        },
                    },
                    {
                        name: 'waybill.edit',
                        path: ':id/edit',
                        props: true,
                        component: () => import('@/pages/waybills/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'waybill.index.title', path: '/waybills' },
                            ],
                        },
                    },
                ],
            },
            // warehouses
            {
                path: '/warehouses',
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                },
                children: [
                    {
                        name: 'warehouse.index',
                        path: '',
                        component: () => import('@/pages/warehouses/index.vue'),
                    },
                    {
                        name: 'warehouse.create',
                        path: 'create',
                        component: () => import('@/pages/warehouses/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'warehouse.index.title', path: '/warehouses' },
                            ],
                        },
                    },
                    {
                        name: 'warehouse.edit',
                        path: ':id/:tab?',
                        props: true,
                        component: () => import('@/pages/warehouses/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'warehouse.index.title', path: '/warehouses' },
                            ],
                        },
                    },
                ],
            },
            // warehouse purchases
            {
                path: '/warehouse-purchases',
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                },
                children: [
                    {
                        name: 'warehouse-purchase.index',
                        path: '',
                        component: () => import('@/pages/warehouse-purchases/index.vue'),
                    },
                    {
                        name: 'warehouse-purchase.create',
                        path: 'create',
                        props(to) {
                            var _a, _b;
                            return { products: ((_a = to.meta) === null || _a === void 0 ? void 0 : _a.products) ? (_b = to.meta) === null || _b === void 0 ? void 0 : _b.products : null };
                        },
                        component: () => import('@/pages/warehouse-purchases/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'warehouse_purchase.index.title', path: '/warehouse-purchases' },
                            ],
                        },
                    },
                    {
                        name: 'warehouse-purchase.edit',
                        path: ':purchaseId/:tab?/edit',
                        props: true,
                        component: () => import('@/pages/warehouse-purchases/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'warehouse_purchase.index.title', path: '/warehouse-purchases' },
                            ],
                        },
                    },
                ],
            },
            // product categories
            {
                path: '/product-categories',
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                },
                children: [
                    {
                        name: 'product-category.index',
                        path: '',
                        component: () => import('@/pages/product-categories/index.vue'),
                    },
                    {
                        name: 'product-category.create',
                        path: 'create',
                        component: () => import('@/pages/product-categories/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'warehouse_category.index.title', path: '/product-categories' },
                            ],
                        },
                    },
                    {
                        name: 'product-category.edit',
                        path: ':id/edit',
                        props: true,
                        component: () => import('@/pages/product-categories/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'warehouse_category.index.title', path: '/product-categories' },
                            ],
                        },
                    },
                ],
            },
            {
                name: 'plan-expired.index',
                path: '/plan-expired',
                component: () => import('@/pages/plan-expired/index.vue'),
                meta: {
                    middleware: [hasActiveCompany],
                },
            },
            // email templates
            {
                path: '/email-templates',
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                    breadcrumb: [
                        { name: 'company.manage.invoices', path: '/company/invoices' },
                    ],
                },
                children: [
                    {
                        name: 'email-templates.create',
                        path: 'create',
                        component: () => import('@/pages/email-template/manage.vue'),
                    },
                    {
                        name: 'email-templates.edit',
                        path: ':id/edit',
                        props: true,
                        component: () => import('@/pages/email-template/manage.vue'),
                    },
                ],
            },
            // reports
            {
                path: '/reports',
                meta: {
                    middleware: [hasActiveCompany],
                },
                children: [
                    {
                        name: 'reports.index',
                        path: '',
                        component: () => import('@/pages/reports/index.vue'),
                    },
                    {
                        name: 'reports.edit',
                        path: ':id/edit',
                        props: true,
                        component: () => import('@/pages/reports/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'reports.index.title', path: '/reports' },
                            ],
                        },
                    },
                    {
                        name: 'reports.create',
                        path: '/create',
                        component: () => import('@/pages/reports/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'reports.index.title', path: '/reports' },
                            ],
                        },
                    },
                ],
            },
            // pdf templates
            {
                path: '/pdf-templates',
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                    breadcrumb: [
                        { name: 'pdf-template.index.pdf_templates', path: '/company/pdf-templates' },
                    ],
                },
                children: [
                    {
                        name: 'invoices-pdf-templates.create',
                        path: 'invoices/create',
                        component: () => import('@/pages/pdf-templates/invoice-templates/manage.vue'),
                    },
                    {
                        name: 'invoices-pdf-templates.edit',
                        path: '/invoices/:id/edit',
                        props: true,
                        component: () => import('@/pages/pdf-templates/invoice-templates/manage.vue'),
                    },
                    {
                        name: 'commercial-pdf-templates.create',
                        path: 'commercial/create',
                        component: () => import('@/pages/pdf-templates/commercial-templates/manage.vue'),
                    },
                    {
                        name: 'commercial-pdf-templates.edit',
                        path: '/commercial/:id/edit',
                        props: true,
                        component: () => import('@/pages/pdf-templates/commercial-templates/manage.vue'),
                    },
                ],
            },
            // work act
            {
                path: '/work-act',
                meta: {
                    middleware: [hasActiveCompany],
                    scroll: {
                        ignoreQuery: ['expanded'],
                    },
                },
                children: [
                    {
                        name: 'work-act.index',
                        path: '',
                        component: () => import('@/pages/work-act/index.vue'),
                    },
                    {
                        name: 'work-act.create',
                        path: 'create',
                        component: () => import('@/pages/work-act/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'work-act.index.acts', path: '/work-act' },
                            ],
                        },
                    },
                    {
                        name: 'work-act.edit',
                        path: ':id/edit',
                        props: true,
                        component: () => import('@/pages/work-act/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'work-act.index.acts', path: '/work-act' },
                            ],
                        },
                    },
                ],
            },
            {
                path: '/sip-statements',
                meta: {
                    middleware: [hasActiveCompany],
                },
                children: [
                    {
                        name: 'sip-statements.payments.index',
                        path: 'payments',
                        component: () => import('@/pages/sip-statements/payments/index.vue'),
                    },
                ],
            },
            {
                path: '/purchase-document-import',
                meta: {
                    middleware: [hasActiveCompany, trialExpired],
                },
                children: [
                    {
                        name: 'purchase-document-import.create',
                        path: '',
                        component: () => import('@/pages/ocr/purchase-ocr/manage.vue'),
                        meta: {
                            breadcrumb: [
                                { name: 'purchase-document-import.index.title', path: '/warehouse-purchases' },
                            ],
                        },
                    },
                ],
            },
        ],
    },
    {
        name: 'onboarding',
        path: '/onboarding',
        component: () => import('@/pages/onboarding/v2/manage.vue'),
        meta: {
            middleware: [mustConfirmEmail, onboardingCompleted],
            auth: {
                required: true,
                redirectGuest: { name: 'login' },
            },
        }
    }
];
